'use client';

/**
 * McpClaudeBuiltFor — three target audiences with the free dev access.
 *
 * Mirrors the audience block from the original AIDeveloperExperience
 * component (development teams / technical founders / IT integration
 * teams) on V3 dark surface. Adds the free dev-access program callout.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import { HiCodeBracket, HiRocketLaunch, HiUserGroup } from 'react-icons/hi2';

type Props = { lang: Locale };

type AudienceKey = 'teams' | 'founders' | 'integrators';

const ITEMS: { key: AudienceKey; Icon: typeof HiUserGroup }[] = [
  { key: 'teams', Icon: HiUserGroup },
  { key: 'founders', Icon: HiRocketLaunch },
  { key: 'integrators', Icon: HiCodeBracket },
];

const McpClaudeBuiltFor = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const label = t('McpClaude/BuiltFor/badge');
  const headlineA = t('McpClaude/BuiltFor/title1');
  const headlineB = t('McpClaude/BuiltFor/title2');
  const sub = t('McpClaude/BuiltFor/subtitle');

  return (
    <section
      id="built-for"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="03" label={label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {headlineA} <span style={{ color: '#B4C1D8' }}>{headlineB}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {sub}
        </p>

        <div className="grid gap-5 mb-10" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {ITEMS.map(({ key, Icon }) => {
            return (
              <article
                key={key}
                className="rounded-2xl p-7"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className="inline-flex items-center justify-center rounded-lg mb-4"
                  style={{
                    width: 40,
                    height: 40,
                    background: 'rgba(34,211,238,0.12)',
                    border: '1px solid rgba(34,211,238,0.30)',
                  }}
                >
                  <Icon style={{ width: 20, height: 20, color: '#22D3EE' }} />
                </div>
                <h3
                  className="font-semibold"
                  style={{
                    fontSize: 19,
                    color: '#F5F7FA',
                    margin: '0 0 8px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {t(`McpClaude/BuiltFor/${key}/title`)}
                </h3>
                <p
                  style={{
                    color: '#B4C1D8',
                    fontSize: 14,
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {t(`McpClaude/BuiltFor/${key}/body`)}
                </p>
              </article>
            );
          })}
        </div>

        <div
          className="rounded-2xl p-6 lg:p-7"
          style={{
            background: 'rgba(34,211,238,0.06)',
            border: '1px solid rgba(34,211,238,0.30)',
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              color: '#22D3EE',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {t('McpClaude/BuiltFor/free_label')}
          </div>
          <h3
            className="font-semibold"
            style={{
              fontSize: 19,
              color: '#F5F7FA',
              margin: '0 0 8px',
              letterSpacing: '-0.01em',
            }}
          >
            {t('McpClaude/BuiltFor/free_title')}
          </h3>
          <p
            style={{
              color: '#B4C1D8',
              fontSize: 14,
              lineHeight: 1.55,
              margin: 0,
              maxWidth: 720,
            }}
          >
            {t('McpClaude/BuiltFor/free_body')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default McpClaudeBuiltFor;
